import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Cctv, DoorOpen } from "lucide-react";
import { CCTV } from "@/constants/testIds";
import CctvPage from "@/pages/CctvPage";
import BranchCheckinPage from "@/pages/BranchCheckinPage";

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

/**
 * CCTV Monitoring hub (2026-06). Hosts two sub-tabs:
 *   • monitoring — the CCTV findings table (gated by the `cctv_monitoring` tab)
 *   • checkin    — Branch open/close check-in (gated by canBranchCheckin, which
 *                  is now driven by CCTV-module access — the "CCTV employee").
 *
 * Each sub-tab only renders if the caller is permitted. `?tab=checkin` pre-
 * selects the Check-in sub-tab (used by the legacy /branch-checkin redirect).
 */
export default function CctvHub() {
    const { t } = useI18n();
    const { me, canBranchCheckin, canCctvManage } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const cctvPermitted = !!me?.nav?.find((n) => n.key === "cctv_monitoring")?.permitted || canCctvManage;

    const availableTabs = useMemo(() => {
        const list = [];
        if (cctvPermitted) list.push("monitoring");
        if (canBranchCheckin) list.push("checkin");
        return list;
    }, [cctvPermitted, canBranchCheckin]);

    const requested = searchParams.get("tab");
    const activeTab = availableTabs.includes(requested) ? requested : availableTabs[0];

    if (!availableTabs.length) return null;

    const onTabChange = (val) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", val);
        setSearchParams(next, { replace: true });
    };

    return (
        <div data-testid={CCTV.hub} className="space-y-5">
            <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-5">
                <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                    {availableTabs.includes("monitoring") ? (
                        <TabsTrigger data-testid={CCTV.hubTab("monitoring")} value="monitoring" className={triggerClass}>
                            <Cctv className="h-4 w-4" />
                            {t("cctv_title")}
                        </TabsTrigger>
                    ) : null}
                    {availableTabs.includes("checkin") ? (
                        <TabsTrigger data-testid={CCTV.hubTab("checkin")} value="checkin" className={triggerClass}>
                            <DoorOpen className="h-4 w-4" />
                            {t("nav_checkin")}
                        </TabsTrigger>
                    ) : null}
                </TabsList>
                {availableTabs.includes("monitoring") ? (
                    <TabsContent value="monitoring" className="mt-0 focus-visible:outline-none">
                        <CctvPage />
                    </TabsContent>
                ) : null}
                {availableTabs.includes("checkin") ? (
                    <TabsContent value="checkin" className="mt-0 focus-visible:outline-none">
                        <BranchCheckinPage />
                    </TabsContent>
                ) : null}
            </Tabs>
        </div>
    );
}
